/**
 * Hook for updating (approving or denying) requests to join a buyer entity.
 *
 * Wraps the UpdateRequestToJoinMutation with per-request loading state and
 * useNotification feedback. On success it refetches both the pending requests
 * list and the team-members list so the UI stays consistent.
 */

import { useCallback, useState } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useUpdateRequestToJoinMutationMutation,
  GetRequestsToJoinDocument,
  GetBuyerUsersQueryDocument,
  type JoinRequestAllowedUpdateStatus,
  type UpdateRequestToJoinMutationMutation,
} from '../../../generated';

const isMutationSuccessful = (
  data?: UpdateRequestToJoinMutationMutation['updateRequestToJoin']
): boolean => data?.__typename === 'UpdateJoinRequestSuccess';

export type UseUpdateJoinRequestReturn = {
  /** Call to approve or deny a join request by its id */
  updateJoinRequest: (
    requestId: string,
    status: JoinRequestAllowedUpdateStatus
  ) => Promise<void>;
  /** The requestId currently being mutated, or null when idle */
  processingRequestId: string | null;
};

export const useUpdateJoinRequest = (
  buyerId: string
): UseUpdateJoinRequestReturn => {
  const { addNotification } = useNotification();
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(
    null
  );

  const [updateRequestToJoin] = useUpdateRequestToJoinMutationMutation();

  const updateJoinRequest = useCallback(
    async (
      requestId: string,
      status: JoinRequestAllowedUpdateStatus
    ): Promise<void> => {
      setProcessingRequestId(requestId);

      try {
        const response = await updateRequestToJoin({
          variables: {
            input: { id: requestId, status },
          },
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: GetRequestsToJoinDocument,
              variables: { buyerId, statusFilter: 'pending' },
            },
            {
              query: GetBuyerUsersQueryDocument,
              variables: { buyerId },
            },
          ],
        });

        if (!isMutationSuccessful(response.data?.updateRequestToJoin)) {
          const errorMessage =
            response.data?.updateRequestToJoin &&
            'message' in response.data.updateRequestToJoin
              ? response.data.updateRequestToJoin.message
              : undefined;
          throw new Error(
            errorMessage ??
              'Failed to update request to join. Please try again.'
          );
        }

        addNotification({
          type: 'success',
          title:
            status === 'approved' ? 'Request Accepted' : 'Request Declined',
          message:
            status === 'approved'
              ? 'The user was successfully added to the buyer entity.'
              : 'The user was not added to the buyer entity.',
          duration: 5000,
        });
      } catch (err) {
        addNotification({
          type: 'error',
          title: 'Failed to Update Request',
          message:
            err instanceof Error
              ? err.message
              : 'Failed to update request. Please check your connection and try again.',
          duration: 7000,
        });
      } finally {
        setProcessingRequestId(null);
      }
    },
    [buyerId, updateRequestToJoin, addNotification]
  );

  return { updateJoinRequest, processingRequestId };
};
